import React from "react";
import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../../shared/CiroButton";
import CiroTable from "../../../shared/CiroTable/CiroTable";
import CiroTableCell from "../../../shared/CiroTable/CiroTableCell";
import CiroTableRow from "../../../shared/CiroTable/CiroTableRow";
import CiroTableHeader from "../../../shared/CiroTable/CiroTableHeader";
import { CheckIcon } from "@heroicons/react/24/outline";
import { IImportState } from "./useImportState";
import pluralize from "pluralize";

interface IImportContactsModalCompletedStepProps {
  importState: IImportState;
  onClose: () => void;
  onSuccess: () => void;
}

const ImportContactsModalCompletedStep: React.FC<
  IImportContactsModalCompletedStepProps
> = ({ importState, onClose, onSuccess }) => {
  const { importProgress } = importState;
  const hasDuplicates = importProgress.duplicateContacts.length > 0;
  const duplicateContacts = importProgress.duplicateContacts;

  return (
    <div className={classNames("p-6")}>
      <div className={classNames("flex", "items-center", "mb-4")}>
        <h2 className={classNames("text-xl", "font-medium", "flex-1")}>
          <span className={classNames("inline-flex", "items-center", "gap-2")}>
            <span
              className={classNames(
                "text-green-500",
                "rounded-full",
                "bg-green-50",
                "p-1",
              )}
            >
              <CheckIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            Import complete
          </span>
        </h2>
      </div>

      <div className={classNames("mt-1", "mb-4")}>
        <p className={classNames("text-gray-600")}>
          Successfully imported{" "}
          {importProgress.completedContacts.length.toLocaleString()}{" "}
          {pluralize("contact", importProgress.completedContacts.length)}
          {hasDuplicates && (
            <span>
              {" "}
              ({importProgress.duplicateContacts.length.toLocaleString()}{" "}
              {pluralize("duplicate", importProgress.duplicateContacts.length)}{" "}
              skipped)
            </span>
          )}
        </p>
      </div>

      {hasDuplicates && (
        <div>
          <div className={classNames("py-2", "text-sm", "text-gray-600")}>
            <p>Skipped duplicate contacts:</p>
          </div>
          <CiroTable>
            <thead>
              <CiroTableRow>
                <CiroTableHeader>First name</CiroTableHeader>
                <CiroTableHeader>Last name</CiroTableHeader>
                <CiroTableHeader>Title</CiroTableHeader>
                <CiroTableHeader>Company</CiroTableHeader>
                <CiroTableHeader>Status</CiroTableHeader>
              </CiroTableRow>
            </thead>
            <tbody>
              {duplicateContacts.map((contact, index) => {
                // Determine which fields to display based on whether it's a duplicate or completed contact
                const firstName = contact.firstName;
                const lastName = contact.lastName;
                const title = contact.title;
                const companyName = contact.companyName;

                return (
                  <CiroTableRow key={index} className={"bg-yellow-50"}>
                    <CiroTableCell>{firstName || "-"}</CiroTableCell>
                    <CiroTableCell>{lastName || "-"}</CiroTableCell>
                    <CiroTableCell>{title || "-"}</CiroTableCell>
                    <CiroTableCell>{companyName || "-"}</CiroTableCell>
                    <CiroTableCell>
                      <span
                        className={classNames("text-yellow-600", "font-medium")}
                      >
                        Duplicate
                      </span>
                    </CiroTableCell>
                  </CiroTableRow>
                );
              })}
            </tbody>
          </CiroTable>
        </div>
      )}

      <div className={classNames("mt-6", "flex", "gap-2", "justify-end")}>
        <CiroButton
          analyticsField={"Close import modal"}
          onClick={() => {
            onSuccess();
            onClose();
          }}
          style={CiroButtonStyleEnum.LOUD}
        >
          Done
        </CiroButton>
      </div>
    </div>
  );
};

export default ImportContactsModalCompletedStep;
